import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendSampleSpecification,
	FrontendUserInfo,
	FrontendUserLicense,
} from "node-library/dist/portal";
import {
	CommonSwitch,
	CustomRadioGroup,
	CustomTextInput,
	UserInfoTextInput,
} from "../../common-components/inputs";
import { Card, Divider } from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
} from "node-library/dist/colors";
import {
	CompanyIcon,
	DeleteIcon,
	DownloadIcon,
	FileIcon,
	FolderIcon,
	InvoiceIcon,
	LockIcon,
	MoveIcon,
	UploadIcon,
	UserIcon,
} from "../../common-components/svgIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	FrontendProfileTab,
	SampleMatrix,
	SampleSubMatrix,
	Severity,
} from "node-library/dist/constants";
import { YellowWarnIcon } from "../../common-components/toast/warnIcons";
import {
	SkyBlueButton,
	CustomIconButton,
	BlueButton,
	KebabDropdownButton,
	TextButton,
} from "../../common-components/button";
import AddIcon from "@mui/icons-material/Add";

import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../common-components/toast/errorIcons";
import UserClientInfoSection from "./userClientInfoSection";
import UploadSpecificationModal from "../../common-components/modals/uploadSpecificationModal";
import {
	getMatrixTranslationText,
	getSubMatrixKeysFromMatrix,
	getSubMatrixTranslationText,
} from "../../helpers/sampleHelper";
import SpecificationFolderRow from "./components/SpecificationFolderRow";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import DeleteSpecificationConfirmModal from "../../common-components/modals/deleteSpecificationConfirmModal";
import { GreenTickIcon } from "../../common-components/toast/successIcons";
import MoveSpecificationModal from "./components/moveSpecificationModal";
import { downloadSpecificationFile } from "../../helpers/frontendFileOperationHelper";

interface Props {
	userSpecifications: Array<FrontendSampleSpecification>;
	setUserSpecifications: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecification>>
	>;
}

export default function UserSpecificationsSection(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [uploadSpecModalOpen, setUploadSpecModalOpen] =
		useState<boolean>(false);
	const [matrix, setMatrix] = useState<SampleMatrix>();
	const [subMatrix, setSubMatrix] = useState<SampleSubMatrix>();

	const [deleteSpecModalOpen, setDeleteSpecModalOpen] =
		useState<boolean>(false);

	const [moveSpecificationModalOpen, setMoveSpecificationModalOpen] =
		useState<boolean>(false);

	const [selectedSpecification, setSelectedSpecification] =
		useState<FrontendSampleSpecification>();
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);
	const [loading, setLoading] = useState<boolean>(false);

	const currentFolderSpecs = props.userSpecifications.filter((spec) => {
		return spec.matrix === matrix && spec.subMatrix === subMatrix;
	});

	async function deleteSpecification(specificationId: string) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest(
				"/specifications/delete-specification",
				{
					specificationId,
				}
			);

			props.setUserSpecifications((prevSpecs) =>
				prevSpecs.filter((spec) => spec.specificationId !== specificationId)
			);

			openSnackbar(
				translation("FileDeleted"),
				Severity.ERROR,
				undefined,
				<RedTrashIcon />
			);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	const currentFolderContents: React.ReactNode = subMatrix ? (
		<div
			className={
				matrix && subMatrix ? `` : `profile-specification-not-selected`
			}
		>
			<div className="profile-spec-file-section-header">
				<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
					<CustomIconButton
						id="profile-spec-folder-back-button"
						size="small"
						onClick={() => {
							setMatrix(undefined);
							setSubMatrix(undefined);
						}}
						icon={<KeyboardArrowLeftIcon />}
					></CustomIconButton>
					<div className="typography-body-02 typography-bold">
						{translation("Files")}
					</div>
				</div>
				<SkyBlueButton
					id={"profile-spec-upload-button"}
					size="small"
					buttonClassName="button-with-left-icon"
					onClick={() => {
						setUploadSpecModalOpen(true);
					}}
				>
					<UploadIcon strokeColor="white" size={24} />
					<span
					// className="new-order-collapsible-text"
					>
						{translation("Upload")}
					</span>
				</SkyBlueButton>
			</div>

			<div id="profile-files-selection-container">
				{currentFolderSpecs.length > 0 ? (
					currentFolderSpecs.map((specs) => {
						return (
							<div
								className="profile-spec-file-row"
								key={specs.specificationId}
							>
								<div className="typography-body-02">{`${specs.fileName}.${specs.format}`}</div>
								<KebabDropdownButton
									menuOptions={[
										{
											key: `download-specification-${specs.specificationId}`,
											onClick: () => {
												downloadSpecificationFile(specs.specificationId);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<DownloadIcon size={20}></DownloadIcon>

													{translation("Download")}
												</div>
											),
										},
										{
											key: `move-folder-${specs.specificationId}`,
											onClick: () => {
												setSelectedSpecification(specs);
												setMoveSpecificationModalOpen(true);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<MoveIcon></MoveIcon>

													{translation("Moveto")}
												</div>
											),
										},
										{
											key: `delete-specification-${specs.specificationId}`,
											onClick: () => {
												setProceedAction(() => () => {
													deleteSpecification(specs.specificationId);
												});
												setDeleteSpecModalOpen(true);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<DeleteIcon></DeleteIcon>
													{translation("Delete")}
												</div>
											),
										},
									]}
									id={`specification-more-action-menu-${specs.specificationId}`}
									onClick={(e) => {}}
								></KebabDropdownButton>
							</div>
						);
					})
				) : (
					<div style={{ color: NeutralColor.NEUTRAL_GREY_500 }}>
						{translation("NoSpecificationFilesInFolder")}
					</div>
				)}
			</div>
		</div>
	) : (
		<></>
	);

	const specificationInformation: React.ReactNode = (
		<div>
			<div>
				<div className="profile-icon-header-row typography-heading-03">
					<FileIcon></FileIcon>
					{translation("FilesAndSpecifications")}
				</div>
				<div
					className="typography-caption"
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				>
					{translation("FilesAndSpecificationsDescription")}
				</div>
			</div>

			<div className="profile-specification-folder-and-files-container">
				<div
					className={
						matrix && subMatrix ? `profile-specification-not-selected` : ``
					}
				>
					<div
						className="typography-body-02 typography-bold"
						style={{ margin: "10px 0px" }}
					>
						{translation("MatrixAndSubMatrixFolders")}
					</div>

					<div id="profile-folder-selection-container">
						{Object.values(SampleMatrix).map((matrixValue, index) => {
							const children: Array<React.ReactNode> =
								getSubMatrixKeysFromMatrix(matrixValue).map(
									(subMatrixValue, index) => {
										return (
											<SpecificationFolderRow
												index={index}
												label={getSubMatrixTranslationText(
													subMatrixValue,
													translation
												)}
												icon={
													<FolderIcon
														strokeColor={PrimaryColor.PRIMARY_200}
														fillColor={PrimaryColor.PRIMARY_600}
													/>
												}
												selected={subMatrix == subMatrixValue}
												onRowClick={() => {
													setMatrix(matrixValue);
													setSubMatrix(subMatrixValue);
												}}
											/>
										);
									}
								);
							return (
								<SpecificationFolderRow
									index={index}
									label={getMatrixTranslationText(matrixValue, translation)}
									selected={false}
									icon={
										<FolderIcon
											strokeColor={SecondaryColor.SECONDARY_300}
											fillColor={SecondaryColor.SECONDARY_800}
										/>
									}
									children={children}
								/>
							);
						})}
					</div>
				</div>
				<Divider
					className="profile-specification-folder-container-divider"
					orientation="vertical"
					flexItem
				></Divider>

				{currentFolderContents}
			</div>
		</div>
	);

	// const uploadModal: React.ReactNode =

	return (
		<>
			{specificationInformation}
			<DeleteSpecificationConfirmModal
				open={deleteSpecModalOpen}
				setOpen={setDeleteSpecModalOpen}
				proceedAction={proceedAction}
			></DeleteSpecificationConfirmModal>
			{matrix && subMatrix && (
				<>
					<UploadSpecificationModal
						open={uploadSpecModalOpen}
						setOpen={setUploadSpecModalOpen}
						matrix={matrix as SampleMatrix}
						subMatrix={subMatrix as SampleSubMatrix}
						folderSelectionDisabled={true}
						currentFolderSpecifications={currentFolderSpecs}
						setFolderSpecifications={props.setUserSpecifications}
						setSelectedSpecificationId={(id) => {}}
					></UploadSpecificationModal>
					{selectedSpecification && (
						<MoveSpecificationModal
							open={moveSpecificationModalOpen}
							setOpen={setMoveSpecificationModalOpen}
							matrix={matrix as SampleMatrix}
							subMatrix={subMatrix as SampleSubMatrix}
							selectedSpecification={
								selectedSpecification as FrontendSampleSpecification
							}
							userSpecifications={props.userSpecifications}
							setUserSpecifications={props.setUserSpecifications}
						></MoveSpecificationModal>
					)}
				</>
			)}
		</>
	);
}
